import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { hasRole } from '../config/permissions';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [subscriptions, setSubscriptions] = useState(new Map());
  const [topicData, setTopicData] = useState(new Map());
  const [lastServerError, setLastServerError] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const subscriptionsRef = useRef(new Map());
  const socketRef = useRef(null);

  // SSE (Server-Sent Events) connection management
  const [sseConnectionState, setSseConnectionState] = useState('disconnected');
  const eventSourceRef = useRef(null);
  const sseListenersRef = useRef(new Map()); // Map of listener id -> callback function

  // Keep refs in sync with state
  useEffect(() => {
    subscriptionsRef.current = subscriptions;
  }, [subscriptions]);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  // Setup SSE connection
  const connectSSE = useCallback(() => {
    if (!user) return;

    // The SSE feed is a staff tool and rejects vendor tokens — don't let a
    // vendor session sit in a reconnect loop against it
    if (user.actorType === 'vendor') return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[SSE] No auth token available');
      return;
    }

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const sseUrl = import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse';
    console.log('[SSE] Connecting to:', sseUrl);

    const eventSource = new EventSource(`${sseUrl}/events?token=${token}`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log('[SSE] Connected');
      setSseConnectionState('connected');
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Message received:', data);

        // Notify all registered listeners
        sseListenersRef.current.forEach((callback) => {
          try {
            callback(data);
          } catch (err) {
            console.error('[SSE] Error in listener callback:', err);
          }
        });
      } catch (err) {
        console.error('[SSE] Error parsing message:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('[SSE] Connection error:', err);
      setSseConnectionState('error');

      // EventSource will automatically reconnect
      // We just need to update the state
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('[SSE] Connection closed, will reconnect...');
        setSseConnectionState('reconnecting');
      }
    };
  }, [user]);

  // Register SSE event listener
  const addSSEListener = useCallback((listenerId, callback) => {
    console.log('[SSE] Adding listener:', listenerId);
    sseListenersRef.current.set(listenerId, callback);

    // Return cleanup function
    return () => {
      console.log('[SSE] Removing listener:', listenerId);
      sseListenersRef.current.delete(listenerId);
    };
  }, []);

  // Remove SSE event listener
  const removeSSEListener = useCallback((listenerId) => {
    console.log('[SSE] Removing listener:', listenerId);
    sseListenersRef.current.delete(listenerId);
  }, []);

  const connect = useCallback(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3012';

    // Staff ride the admin endpoint, vendors their own subscribe-only one
    const isVendor = user.actorType === 'vendor';
    const isStaff = hasRole(user.role, 'employee');
    const endpoint = isVendor
      ? '/ws/vendor-subscriber'
      : isStaff ? '/ws/admin-subscriber' : '/ws/subscriber';
    
    console.log('[WebSocket] Connecting with user role:', user.role, 'to endpoint:', endpoint);
    const ws = new WebSocket(`${wsUrl}${endpoint}?token=${token}`);
    
    ws.onopen = () => {
      console.log('[WebSocket] Connected');
      setConnectionState('connected');
      reconnectAttemptsRef.current = 0;
      
      // Re-subscribe to previous subscriptions using ref.
      // The cloud reads the verb from "action" and rejects anything without
      // it, so this has to be the same shape subscribe() sends — a message
      // naming the verb any other way is thrown out as invalid and the
      // streams never come back after a reconnect.
      const currentSubscriptions = subscriptionsRef.current;
      if (currentSubscriptions.size > 0) {
        currentSubscriptions.forEach((topics, serial) => {
          ws.send(JSON.stringify({
            action: 'subscribe',
            serial,
            topics: Array.from(topics),
            userId: user.userId || user._id,
            role: user.role
          }));
        });
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch (err) {
        console.error('[WebSocket] Error parsing message:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      setConnectionState('error');
    };

    ws.onclose = () => {
      console.log('[WebSocket] Disconnected');
      setConnectionState('disconnected');
      setSocket(null);

      // Closed on purpose (logout / unmount) — don't reconnect
      if (ws.intentionalClose) return;

      // Keep retrying forever, backing off to at most 30s between attempts.
      // A monitoring page must come back on its own after a network blip
      // or laptop sleep, not stay dead until a hard refresh.
      const delay = Math.min(1000 * Math.pow(2, Math.min(reconnectAttemptsRef.current, 10)), 30000);
      reconnectAttemptsRef.current++;

      reconnectTimeoutRef.current = setTimeout(() => {
        console.log(`[WebSocket] Reconnecting... (attempt ${reconnectAttemptsRef.current})`);
        connect();
      }, delay);
    };

    setSocket(ws);
  }, [user]);

  const handleMessage = (data) => {
    console.log('[WebSocket] Message received:', data);
    
    switch (data.type) {
      case 'subscribed':
        console.log(`[WebSocket] Subscribed to ${data.serial}:${data.topics.join(',')}`);
        break;
        
      case 'unsubscribed':
        console.log(`[WebSocket] Unsubscribed from ${data.serial}:${data.topics.join(',')}`);
        break;
        
      case 'streamData':
      case 'data':  // Handle both 'streamData' and 'data' message types
        // Store the stream data for the topic
        const topicType = data.streamType || data.topic; // Support both field names
        const topicKey = `${data.serial}:${topicType}`;
        const topicPayload = data.data || data.payload; // Support both field names
        
        setTopicData(prev => {
          const newMap = new Map(prev);
          newMap.set(topicKey, {
            data: topicPayload,
            timestamp: data.timestamp || new Date().toISOString(),
            serial: data.serial,
            topic: topicType
          });
          return newMap;
        });
        console.log('[WebSocket] Received data for', topicKey, topicPayload);
        break;
        
      case 'error':
        console.error('[WebSocket] Error:', data.message);
        setLastServerError({
          message: data.message || 'Unknown error from the cloud',
          timestamp: new Date().toISOString()
        });
        break;
        
      case 'refresh_status':
        // Handle connection refresh status updates
        console.log('[WebSocket] Refresh status:', data);
        // Store refresh status for components to access
        const refreshKey = `${data.serial}:refresh_status`;
        setTopicData(prev => {
          const newMap = new Map(prev);
          newMap.set(refreshKey, {
            data: data,
            timestamp: data.timestamp || new Date().toISOString(),
            serial: data.serial,
            topic: 'refresh_status'
          });
          return newMap;
        });
        break;
        
      default:
        console.log('[WebSocket] Unknown message type:', data.type);
    }
  };

  const subscribe = useCallback((serial, topics) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] Cannot subscribe: socket not connected');
      return false;
    }

    const topicsArray = Array.isArray(topics) ? topics : [topics];
    
    // Update local subscriptions state
    setSubscriptions(prev => {
      const newMap = new Map(prev);
      const existingTopics = newMap.get(serial) || new Set();
      topicsArray.forEach(topic => existingTopics.add(topic));
      newMap.set(serial, existingTopics);
      return newMap;
    });

    // Send subscribe message
    socket.send(JSON.stringify({
      action: 'subscribe',
      serial,
      topics: topicsArray,
      userId: user.userId || user._id,
      role: user.role
    }));

    return true;
  }, [socket, user]);

  const unsubscribe = useCallback((serial, topics) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] Cannot unsubscribe: socket not connected');
      return false;
    }

    const topicsArray = Array.isArray(topics) ? topics : [topics];
    
    // Update local subscriptions state
    setSubscriptions(prev => {
      const newMap = new Map(prev);
      const existingTopics = newMap.get(serial);
      if (existingTopics) {
        topicsArray.forEach(topic => existingTopics.delete(topic));
        if (existingTopics.size === 0) {
          newMap.delete(serial);
        } else {
          newMap.set(serial, existingTopics);
        }
      }
      return newMap;
    });

    // Send unsubscribe message
    socket.send(JSON.stringify({
      action: 'unsubscribe',
      serial,
      topics: topicsArray,
      userId: user.userId || user._id
    }));

    // Clear topic data
    topicsArray.forEach(topic => {
      const key = `${serial}:${topic}`;
      setTopicData(prev => {
        const newMap = new Map(prev);
        newMap.delete(key);
        return newMap;
      });
    });

    return true;
  }, [socket, user]);

  const getTopicData = useCallback((serial, topic) => {
    const key = `${serial}:${topic}`;
    return topicData.get(key);
  }, [topicData]);

  const isSubscribed = useCallback((serial, topic) => {
    const topics = subscriptions.get(serial);
    return topics ? topics.has(topic) : false;
  }, [subscriptions]);

  const getSubscriptions = useCallback((serial) => {
    const topics = subscriptions.get(serial);
    return topics ? Array.from(topics) : [];
  }, [subscriptions]);

  // Connect WebSocket on mount and when user changes
  useEffect(() => {
    if (user) {
      // Only connect if we don't have an active socket
      const currentSocket = socketRef.current;
      if (!currentSocket || currentSocket.readyState !== WebSocket.OPEN) {
        connect();
      }
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      const currentSocket = socketRef.current;
      if (currentSocket) {
        // Clear subscriptions before closing
        setSubscriptions(new Map());
        setTopicData(new Map());
        currentSocket.intentionalClose = true;
        currentSocket.close();
      }
    };
  }, [user, connect]);

  // Connect SSE on mount and when user changes
  useEffect(() => {
    if (user) {
      connectSSE();
    }

    return () => {
      // Cleanup SSE connection
      if (eventSourceRef.current) {
        console.log('[SSE] Closing connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      // Clear all listeners
      sseListenersRef.current.clear();
      setSseConnectionState('disconnected');
    };
  }, [user, connectSSE]);

  // Send update command to device through cloud
  const sendUpdate = useCallback((serial, topic, updates) => {
    // Vendor sessions are read-only; the cloud rejects these frames anyway
    if (user?.actorType === 'vendor') {
      return Promise.reject(new Error('Vendor access is read-only'));
    }
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] Cannot send update: socket not connected');
      return Promise.reject(new Error('WebSocket not connected'));
    }

    return new Promise((resolve, reject) => {
      // Generate commandId to match what cloud expects: start-${serial}-${domain}-${Date.now()}
      const commandId = `start-${serial}-dashboard-${Date.now()}`;
      
      // Set up one-time ACK listener
      const handleAck = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ack' && data.commandId === commandId) {
            socket.removeEventListener('message', handleAck);
            // The box answers with one result per path and a summary; a refused
            // change arrives as an ack too, so read the verdict before calling it done.
            const results = Array.isArray(data.updateResults) ? data.updateResults : [];
            const failed = results.filter((r) => r && r.success === false);
            if (failed.length > 0 || data.summary?.failed > 0) {
              const reason = failed.map((r) => r.error).filter(Boolean).join('; ');
              console.warn('[WebSocket] Update refused by the device:', commandId, reason);
              reject(new Error(reason || 'The device refused the change'));
              return;
            }
            console.log('[WebSocket] Update acknowledged:', commandId);
            resolve({ success: true, commandId, updateResults: results, summary: data.summary });
          }
        } catch (err) {
          // Ignore parse errors for non-JSON messages
        }
      };
      
      socket.addEventListener('message', handleAck);
      
      // Send update_api message with commandId
      const message = {
        action: 'update_api',
        commandId,  // Include the commandId in the message
        serial,
        topic,
        updates,
        userId: user.userId || user._id,
        role: user.role,
        domain: 'dashboard'
      };
      
      console.log('[WebSocket] Sending update:', message);
      socket.send(JSON.stringify(message));
      
      // Timeout after 10 seconds 
      setTimeout(() => {
        socket.removeEventListener('message', handleAck);
        reject(new Error('Update timeout - no acknowledgment received'));
      }, 10000);
    });
  }, [socket, user]);

  const value = {
    socket,
    connectionState,
    subscribe,
    unsubscribe,
    getTopicData,
    isSubscribed,
    getSubscriptions,
    subscriptions,
    topicData,
    lastServerError,
    sendUpdate,
    // SSE methods
    sseConnectionState,
    addSSEListener,
    removeSSEListener
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};